import { inject, Injectable } from '@angular/core';

import { SupportedLanguageCodes } from '@ph-static-data/languages';
import { EnglishMonthAbbreviations, FrenchMonthAbbreviations, FrenchMonthNames } from '@ph-static-data/month-names';

import { ConfigService } from '@ph-core/services/config/config.service';
import { matchesVinSearch } from '@ph-core/utils/secondary-vin';
import { ContractType } from '@ph-model/api';
import { PendingContractResponse } from '@ph-model/api/response/pending-contract.response.model';
import { ContractFilter } from '@ph-model/filters/contract-filter.model';
import { Filter } from '@ph-model/filters/filter';
import { SearchFilter } from '@ph-model/filters/search-filter';
import { AccessScope } from '@ph-model/login/access-scopes';
import { StatusChipData } from '@ph-shared/components/status-chip/status-chip.interface';
import { UserStore } from '@ph-store/user/user.store';

import { hideDealerCostProductCodes } from 'app/static-data/hide-dealer-cost';

@Injectable({
  providedIn: 'root',
})
export class RemitService {
  userStore = inject(UserStore);
  scopes = this.userStore.user()?.scopes;

  suppressDealerCost(contracts: ContractType[]): ContractType[] {
    return this._suppressDealerCost(contracts, this.scopes || []);
  }

  private _suppressDealerCost(contracts: ContractType[], scopes: AccessScope[]): ContractType[] {
    contracts.forEach((contract: ContractType) => {
      contract.hideDealerCost =
        hideDealerCostProductCodes.includes(contract.productCode) || scopes.includes('HIDE_DEALER_COST');
      contract.contractDealerCost = contract.hideDealerCost ? '0' : contract.contractDealerCost;
    });

    return contracts;
  }

  static fetchPendingContractFilters(contracts: ContractType[]): SearchFilter {
    return {
      month: contracts
        .reduce((prevVal: ContractFilter[], elem: ContractType) => {
          if (elem.contractGroup1) {
            const index = prevVal.some((item) => item.name === elem.contractGroup1);
            if (!index) {
              prevVal.push(new ContractFilter(elem.contractGroup1));
            }
          }

          return prevVal;
        }, [])
        .sort((a: Filter, b: Filter) => new Date(b.name).getTime() - new Date(a.name).getTime()),
      productCode: contracts
        .reduce((prevVal: Filter[], elem: ContractType) => {
          if (elem.productCode) {
            const index = prevVal.some((item) => item.name === elem.productCode);
            if (!index) {
              prevVal.push(new Filter(elem.productCode, false, true, '', elem.description));
            }
          }

          return prevVal;
        }, [])
        .sort((a: Filter, b: Filter) => a.name.localeCompare(b.name)),
      productType: contracts
        .reduce((prevVal: Filter[], elem: ContractType) => {
          if (elem.productCategory) {
            const index = prevVal.some((item) => item.name === elem.productCategory);
            if (!index) {
              prevVal.push(new Filter(elem.productCategory));
            }
          }

          return prevVal;
        }, [])
        .sort((a: Filter, b: Filter) => a.name.localeCompare(b.name)),
      status: this.fetchPendingStatusFilters(contracts),
      searchPhrase: '',
    };
  }

  static fetchRemittedContractFilters(contracts: ContractType[]): SearchFilter {
    return {
      month: contracts
        .reduce((prevVal: ContractFilter[], elem: ContractType) => {
          if (elem.contractGroup1) {
            const index = prevVal.some((item) => item.name === elem.contractGroup1);
            if (!index) {
              const filterGroup = this.fetchRemittedContractSubfilters(elem.contractGroup1, contracts);
              prevVal.push(new ContractFilter(elem.contractGroup1, false, filterGroup));
            }
          }

          return prevVal;
        }, [])
        .sort((a: ContractFilter, b: ContractFilter) => new Date(b.name).getTime() - new Date(a.name).getTime()),
      productCode: contracts
        .reduce((prevVal: Filter[], elem: ContractType) => {
          if (elem.productCode) {
            const index = prevVal.some((item) => item.name === elem.productCode);
            if (!index) {
              prevVal.push(new Filter(elem.productCode, false, true, '', elem.description));
            }
          }

          return prevVal;
        }, [])
        .sort((a: Filter, b: Filter) => a.name.localeCompare(b.name)),
      productType: contracts
        .reduce((prevVal: Filter[], elem: ContractType) => {
          if (elem.productCategory) {
            const index = prevVal.some((item) => item.name === elem.productCategory);
            if (!index) {
              prevVal.push(new Filter(elem.productCategory));
            }
          }

          return prevVal;
        }, [])
        .sort((a: Filter, b: Filter) => a.name.localeCompare(b.name)),
      status: [],
      searchPhrase: '',
    };
  }

  static fetchRemittedContractSubfilters(contractGroup1: string, contracts: ContractType[]): Filter[] {
    return contracts
      .reduce((prevVal: Filter[], elem: ContractType) => {
        if (elem.contractGroup1 && elem.contractGroup1 === contractGroup1) {
          const index = prevVal.some((item) => item.name === elem.contractGroup2);
          if (!index) {
            prevVal.push(new Filter(elem.contractGroup2));
          }
        }

        return prevVal;
      }, [])
      .sort((a: Filter, b: Filter) => new Date(b.name).getTime() - new Date(a.name).getTime());
  }

  static filterBySearchTerm(searchTerm: string, contracts: ContractType[]): ContractType[] {
    if (!searchTerm) {
      return contracts;
    }

    return contracts.filter((contract: ContractType) => {
      return (
        `${contract.firstName} ${contract.lastName} ${contract.businessName}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) || matchesVinSearch(searchTerm, contract.vin, contract.secondaryVin)
      );
    });
  }

  ////////// STATIC FILTER PRODUCTS ////////////////////////////////////////////////////////
  static filterPendingGrid(
    pendingContractFilters: SearchFilter,
    searchTerm: string,
    contracts: ContractType[]
  ): ContractType[] {
    let filteredContracts = this.filterBySearchTerm(searchTerm, contracts);

    const checkedMonths = pendingContractFilters.month.filter((filter) => filter.selected);
    const checkedProductCodes = pendingContractFilters.productCode.filter((filter) => filter.selected);
    const checkedProductTypes = pendingContractFilters.productType.filter((filter) => filter.selected);
    const includedStatuses = pendingContractFilters.status.filter((filter) => filter.selected);
    const excludedStatuses = pendingContractFilters.status.filter((filter) => filter.excluded);
    if (checkedMonths.length > 0) {
      filteredContracts = filteredContracts.filter((contract) =>
        checkedMonths.some((filter) => filter.name === contract?.contractGroup1)
      );
    }
    if (checkedProductTypes.length > 0) {
      filteredContracts = filteredContracts.filter((contract) =>
        checkedProductTypes.some((filter) => filter.name === contract?.productCategory)
      );
    }
    if (checkedProductCodes.length > 0) {
      filteredContracts = filteredContracts.filter((contract) =>
        checkedProductCodes.some((filter) => filter.name === contract?.productCode)
      );
    }
    if (includedStatuses.length > 0) {
      filteredContracts = filteredContracts.filter((contract) =>
        includedStatuses.some((filter) => this.contractMatchesStatusFilter(contract, filter))
      );
    }
    if (excludedStatuses.length > 0) {
      filteredContracts = filteredContracts.filter(
        (contract) => !excludedStatuses.some((filter) => this.contractMatchesStatusFilter(contract, filter))
      );
    }

    return filteredContracts;
  }

  static filterRemittedGrid(
    remittedContractFilter: SearchFilter,
    searchTerm: string,
    contracts: ContractType[]
  ): ContractType[] {
    let filteredContracts = this.filterBySearchTerm(searchTerm, contracts);

    const checkedMonths = remittedContractFilter.month.filter((filter) => filter.selected);
    const checkedProductCodes = remittedContractFilter.productCode.filter((filter) => filter.selected);

    if (checkedMonths.length > 0) {
      filteredContracts = filteredContracts.filter((contract) =>
        checkedMonths.some((filter) => filter.name === contract.contractGroup1)
      );

      const checkedDayFilters = ([] as Filter[]).concat(
        ...checkedMonths.map((monthFilter: Filter | ContractFilter) =>
          'batch' in monthFilter ? monthFilter.batch.filter((dayFilter) => dayFilter.selected) : []
        )
      );

      if (checkedDayFilters.length > 0) {
        filteredContracts = filteredContracts.filter((contract) =>
          checkedDayFilters.some((filter) => filter.name === contract?.contractGroup2)
        );
      }
    }
    if (checkedProductCodes.length > 0) {
      filteredContracts = filteredContracts.filter((contract) =>
        checkedProductCodes.some((filter) => filter.name === contract?.productCode)
      );
    }

    return filteredContracts;
  }

  // /////////////////////////////////////////////////////////////////////////
  ///////////////////  VALIDATION FUNCTIONS  /////////////////////////////////
  static declineAndUpdateContracts(
    pendingGridContracts: ContractType[],
    contractToDecline: ContractType
  ): ContractType[] {
    contractToDecline.decline = true;

    const foundContractIndx = pendingGridContracts.findIndex(
      (contract: ContractType) => contract.econContractNumber === contractToDecline.econContractNumber
    );

    pendingGridContracts[foundContractIndx] = contractToDecline;

    return pendingGridContracts;
  }

  static reinstateAndUpdateContracts(
    pendingGridContracts: ContractType[],
    contractToReinstate: ContractType
  ): ContractType[] {
    // check for duplicate array to see if have not been remitted/declined.
    // if any still in this list and was a original dup with no decline/remit, then change the
    // contract to duplicate.
    if (contractToReinstate.origDup) {
      let actionTakenOnAllDuplicates = true;

      contractToReinstate.dupArray.forEach((dupContractNumber: string) => {
        const otherDupContract = pendingGridContracts.findIndex(
          (contract: ContractType) => contract.econContractNumber === dupContractNumber
        );
        if (otherDupContract > -1) {
          const currentContract = pendingGridContracts[otherDupContract];
          // if action hasnt been taken on ANY contracts whether its declined or remitted
          if (currentContract.origDup && currentContract.addToRemit) {
            pendingGridContracts[otherDupContract].addToRemit = false;
          }
        }
      });

      contractToReinstate.dupArray.forEach((dupContractNumber: string) => {
        const otherDupContract = pendingGridContracts.findIndex(
          (contract: ContractType) => contract.econContractNumber === dupContractNumber
        );
        if (otherDupContract > -1) {
          const currentContract = pendingGridContracts[otherDupContract];
          // if action hasn't been taken on ANY contracts whether its declined or remitted
          if (currentContract.origDup && !currentContract.decline && !currentContract.addToRemit) {
            pendingGridContracts[otherDupContract].dupError = true;
            actionTakenOnAllDuplicates = false;
          }
        }
      });

      // if action has been taken on all duplicates then do not set duplicate to true
      if (actionTakenOnAllDuplicates) {
        contractToReinstate.decline = false;
        contractToReinstate.dupError = false;
      } else {
        contractToReinstate.decline = false;
        contractToReinstate.dupError = true;
      }
    } else {
      contractToReinstate.decline = false;
      contractToReinstate.dupError = false;
    }

    const foundContractIndx = pendingGridContracts.findIndex(
      (contract: ContractType) => contract.econContractNumber === contractToReinstate.econContractNumber
    );

    pendingGridContracts[foundContractIndx] = contractToReinstate;

    return pendingGridContracts;
  }

  ///////////////////  RESET CONTRACTS  /////////////////////////////////
  static resetContracts(response: PendingContractResponse): PendingContractResponse {
    response.pendingContracts = response.pendingContracts.map((contract: ContractType) => {
      contract.addToRemit = false;
      contract.decline = false;

      return contract;
    });

    return response;
  }

  static isPriorSaleDate(contractType: ContractType): boolean {
    let saleDate = contractType.saleDate;
    if (ConfigService.getLanguage() === SupportedLanguageCodes.French) {
      if (contractType.saleDate.includes(' ')) {
        const date: string[] = contractType.saleDate.split(' ');
        const month = date[1];
        if (FrenchMonthNames.includes(month)) {
          const index = FrenchMonthNames.findIndex((m: string) => m === month);
          saleDate = `${date[0]} ${EnglishMonthAbbreviations[index]} ${date[2]}`;
        } else if (FrenchMonthAbbreviations.includes(month)) {
          const index = FrenchMonthAbbreviations.findIndex((m: string) => m === month);
          saleDate = `${date[0]} ${EnglishMonthAbbreviations[index]} ${date[2]}`;
        }
      }
    }

    return new Date() < new Date(saleDate);
  }

  private static fetchPendingStatusFilters(contracts: ContractType[]): Filter[] {
    return contracts
      .reduce((prevVal: Filter[], elem: ContractType) => {
        elem.statuses?.forEach((tag: StatusChipData) => {
          const alreadyListed = prevVal.some((item) => item.name === tag.label);
          if (!alreadyListed && tag.label) {
            prevVal.push(new Filter(tag.label));
          }
        });

        return prevVal;
      }, [])
      .sort((a: Filter, b: Filter) => a.name.localeCompare(b.name));
  }

  private static contractMatchesStatusFilter(contract: ContractType, filter: Filter): boolean {
    return !!contract.statuses?.some((status) => status.label === filter.name);
  }
}
